import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "../utils/axiosInstance";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaSave,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaEye,
  FaFilter,
  FaSortAmountDown,
  FaSortAmountUp,
} from "react-icons/fa";

import { getApiBase } from "../utils/useApiBase";
const API = `${getApiBase()}/kurwanya-umwanda`;

const initialForm = {
  izina_rya_umuturage: "",
  citizen_id: "",
  ukorana_na_kampani_itwara_imyanda: "",
  izina_rya_kampani: "",
  ufite_igikoni: "",
  unywa_amazi_meza: "",
  ufite_agatanda_kamasahane_cyangwa_akabati: "",
};

// ─── Device Detection Utility ───
const getDeviceInfo = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;

  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /android/i.test(ua);
  const isWindows = /windows/i.test(ua);
  const isMac = /macintosh/i.test(ua);
  const isLinux = /linux/i.test(ua) && !isAndroid;
  const isChromeOS = /CrOS/.test(ua);

  const isSamsungBrowser = /SamsungBrowser/i.test(ua);
  const isChrome = /Chrome/i.test(ua) && !/Edge|Edg/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome/i.test(ua);
  const isFirefox = /Firefox/i.test(ua);
  const isEdge = /Edge|Edg/i.test(ua);
  const isOpera = /OPR|Opera/i.test(ua);

  const hasTouch =
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0;

  const pixelRatio = window.devicePixelRatio || 1;

  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;

  return {
    isIOS,
    isAndroid,
    isWindows,
    isMac,
    isLinux,
    isChromeOS,
    isSamsungBrowser,
    isChrome,
    isSafari,
    isFirefox,
    isEdge,
    isOpera,
    hasTouch,
    pixelRatio,
    isStandalone,
    isMobileOS: isIOS || isAndroid,
  };
};

// ─── Custom Hook: Full Responsive System ───
const useResponsive = () => {
  const [state, setState] = useState(() => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1200;
    const h = typeof window !== "undefined" ? window.innerHeight : 800;
    return {
      width: w,
      height: h,
      orientation: w > h ? "landscape" : "portrait",
      device: getDeviceInfo(),
    };
  });

  useEffect(() => {
    let rafId = null;
    let timeoutId = null;

    const update = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setState({
        width: w,
        height: h,
        orientation: w > h ? "landscape" : "portrait",
        device: getDeviceInfo(),
      });
    };

    const handleResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        rafId = requestAnimationFrame(update);
      }, 80);
    };

    window.addEventListener("resize", handleResize, { passive: true });
    window.addEventListener("orientationchange", () => {
      setTimeout(update, 200);
    });

    const mql = window.matchMedia("(orientation: portrait)");
    if (mql.addEventListener) {
      mql.addEventListener("change", handleResize);
    }

    update();

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, []);

  const { width, height, orientation, device } = state;

  // ─── Breakpoints for EVERY device ───
  const breakpoints = {
    // Tiny watches / Galaxy Fold closed
    isWatch: width <= 200,
    // Galaxy Fold closed (280px), iPhone SE old (320px)
    isFoldClosed: width > 200 && width <= 300,
    // iPhone SE, Galaxy S mini, small Androids (320-360)
    isExtraSmall: width > 300 && width <= 360,
    // iPhone 12 Mini, Pixel 5, Galaxy S21 (360-390)
    isSmallMobile: width > 360 && width <= 390,
    // iPhone 12/13/14, Pixel 6, Galaxy S22 (390-414)
    isMediumMobile: width > 390 && width <= 414,
    // iPhone 14 Plus/Pro Max, Pixel 7 Pro (414-480)
    isLargeMobile: width > 414 && width <= 480,
    // Large Android phones landscape start, phablets (480-600)
    isPhablet: width > 480 && width <= 600,
    // Small tablets, iPad Mini, Fire 7 (600-768)
    isSmallTablet: width > 600 && width <= 768,
    // iPad, Galaxy Tab, Surface Go (768-834)
    isTablet: width > 768 && width <= 834,
    // iPad Air, iPad Pro 11, Surface Pro (834-1024)
    isLargeTablet: width > 834 && width <= 1024,
    // iPad Pro 12.9, small laptops (1024-1200)
    isSmallDesktop: width > 1024 && width <= 1200,
    // Standard desktop (1200-1440)
    isDesktop: width > 1200 && width <= 1440,
    // Large desktop, iMac, ultrawide start (1440-1920)
    isLargeDesktop: width > 1440 && width <= 1920,
    // 4K, ultrawide monitors (1920+)
    isUltraWide: width > 1920,
  };

  // ─── Grouped queries ───
  const isMobileAny =
    breakpoints.isWatch ||
    breakpoints.isFoldClosed ||
    breakpoints.isExtraSmall ||
    breakpoints.isSmallMobile ||
    breakpoints.isMediumMobile ||
    breakpoints.isLargeMobile;

  const isTabletAny =
    breakpoints.isPhablet ||
    breakpoints.isSmallTablet ||
    breakpoints.isTablet ||
    breakpoints.isLargeTablet;

  const isDesktopAny =
    breakpoints.isSmallDesktop ||
    breakpoints.isDesktop ||
    breakpoints.isLargeDesktop ||
    breakpoints.isUltraWide;

  const isTouchDevice = device.hasTouch || isMobileAny || isTabletAny;

  const isLandscape = orientation === "landscape";
  const isPortrait = orientation === "portrait";

  // Short height detection (keyboard open, landscape phone)
  const isShortScreen = height < 500;
  const isMediumHeight = height >= 500 && height < 700;

  return {
    width,
    height,
    orientation,
    device,
    ...breakpoints,
    isMobileAny,
    isTabletAny,
    isDesktopAny,
    isTouchDevice,
    isLandscape,
    isPortrait,
    isShortScreen,
    isMediumHeight,
  };
};

// ─── Scroll Lock Hook ───
const useScrollLock = (isLocked) => {
  useEffect(() => {
    if (isLocked) {
      const scrollY = window.scrollY;
      const body = document.body;
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.left = "0";
      body.style.right = "0";
      body.style.overflow = "hidden";
      body.style.width = "100%";

      return () => {
        body.style.position = "";
        body.style.top = "";
        body.style.left = "";
        body.style.right = "";
        body.style.overflow = "";
        body.style.width = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [isLocked]);
};

const KurwanyaUmwanda = () => {
  const responsive = useResponsive();
  const {
    width,
    height,
    device,
    isWatch,
    isFoldClosed,
    isExtraSmall,
    isSmallMobile,
    isMediumMobile,
    isLargeMobile,
    isPhablet,
    isSmallTablet,
    isTablet,
    isLargeTablet,
    isSmallDesktop,
    isDesktop,
    isLargeDesktop,
    isUltraWide,
    isMobileAny,
    isTabletAny,
    isDesktopAny,
    isTouchDevice,
    isLandscape,
    isPortrait,
    isShortScreen,
  } = responsive;

  // ─── State ───
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sortField, setSortField] = useState("id");
  const [sortDir, setSortDir] = useState("desc");
  const [filterKampani, setFilterKampani] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  const searchRef = useRef(null);
  const modalRef = useRef(null);

  // Lock scroll when modals open
  useScrollLock(showModal || showViewModal || deleteConfirm !== null);

  // Pagination
  const getPerPage = () => {
    if (isWatch || isFoldClosed) return 2;
    if (isExtraSmall || isSmallMobile) return 3;
    if (isMediumMobile || isLargeMobile) return 4;
    if (isPhablet) return 4;
    if (isSmallTablet) return 6;
    if (isTablet || isLargeTablet) return 6;
    if (isLandscape && isMobileAny) return 4;
    if (isSmallDesktop) return 8;
    if (isDesktop) return 9;
    if (isLargeDesktop) return 12;
    if (isUltraWide) return 16;
    return 6;
  };

  const [currentPage, setCurrentPage] = useState(1);
  const perPage = getPerPage();

  // ─── Token ───
  const getHeaders = useCallback(() => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  // ─── Fetch ───
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API, getHeaders());
      setRecords(Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
    } catch {
      showMessage("❌ Ntibishoboka kubona amakuru", "error");
    }
    setLoading(false);
  }, [getHeaders]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Household heads registered under Isibo, for the auto-populate
  // dropdown instead of hand-typing a name.
  const [householdHeads, setHouseholdHeads] = useState([]);
  useEffect(() => {
    axios.get(`${getApiBase()}/isibo/household-heads`)
      .then((res) => setHouseholdHeads(Array.isArray(res.data) ? res.data : []))
      .catch(() => setHouseholdHeads([]));
  }, []);

  // ─── Message ───
  const showMessage = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: "", type: "" }), 4000);
  };

  // ─── Handlers ───
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "ukorana_na_kampani_itwara_imyanda" && value === "Oya") {
        updated.izina_rya_kampani = "";
      }
      return updated;
    });
  };

  const handleCitizenSelect = (e) => {
    const citizenId = e.target.value;
    const citizen = householdHeads.find((c) => String(c.umuturage_id) === String(citizenId));
    setForm((prev) => ({
      ...prev,
      citizen_id: citizenId,
      izina_rya_umuturage: citizen ? citizen.full_name : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.izina_rya_umuturage.trim()) {
      showMessage("⚠️ Andika izina ry'umuturage", "error");
      return;
    }
    if (!form.ukorana_na_kampani_itwara_imyanda) {
      showMessage("⚠️ Hitamo niba ukorana na kampani", "error");
      return;
    }
    if (
      form.ukorana_na_kampani_itwara_imyanda === "Yego" &&
      !form.izina_rya_kampani.trim()
    ) {
      showMessage("⚠️ Andika izina rya kampani", "error");
      return;
    }
    if (!form.ufite_igikoni) {
      showMessage("⚠️ Hitamo niba ufite igikoni", "error");
      return;
    }
    if (!form.unywa_amazi_meza) {
      showMessage("⚠️ Hitamo niba unywa amazi meza", "error");
      return;
    }
    if (!form.ufite_agatanda_kamasahane_cyangwa_akabati) {
      showMessage("⚠️ Hitamo niba ufite agatanda/amasahane/akabati", "error");
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        izina_rya_umuturage: form.izina_rya_umuturage.trim(),
        citizen_id: form.citizen_id || null,
        ukorana_na_kampani_itwara_imyanda:
          form.ukorana_na_kampani_itwara_imyanda,
        izina_rya_kampani:
          form.ukorana_na_kampani_itwara_imyanda === "Yego"
            ? form.izina_rya_kampani.trim()
            : null,
        ufite_igikoni: form.ufite_igikoni,
        unywa_amazi_meza: form.unywa_amazi_meza,
        ufite_agatanda_kamasahane_cyangwa_akabati:
          form.ufite_agatanda_kamasahane_cyangwa_akabati,
      };

      if (editId) {
        const validId = parseInt(editId, 10);
        if (isNaN(validId) || validId <= 0) {
          showMessage("❌ ID ntiyemewe.", "error");
          setLoading(false);
          return;
        }
        await axios.put(`${API}/${validId}`, dataToSend, getHeaders());
        showMessage("✏️ Amakuru yavuguruwe neza!");
      } else {
        const response = await axios.post(API, dataToSend, getHeaders());
        if (response.data?.id === 0) {
          showMessage("⚠️ Amakuru yanditswe ariko ID ni 0.", "error");
        } else {
          showMessage("✅ Amakuru yanditswe neza!");
        }
      }
      resetForm();
      await fetchRecords();
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || err.response?.data?.error || "Habaye ikosa";
      showMessage(`❌ ${errorMsg}`, "error");
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    const validId = parseInt(record.id, 10);
    if (isNaN(validId) || validId <= 0) {
      showMessage("❌ ID ntiyemewe.", "error");
      return;
    }
    setForm({
      izina_rya_umuturage: record.izina_rya_umuturage || "",
      citizen_id: record.citizen_id || "",
      ukorana_na_kampani_itwara_imyanda:
        record.ukorana_na_kampani_itwara_imyanda || "",
      izina_rya_kampani: record.izina_rya_kampani || "",
      ufite_igikoni: record.ufite_igikoni || "",
      unywa_amazi_meza: record.unywa_amazi_meza || "",
      ufite_agatanda_kamasahane_cyangwa_akabati:
        record.ufite_agatanda_kamasahane_cyangwa_akabati || "",
    });
    setEditId(validId);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const validId = parseInt(id, 10);
    if (isNaN(validId) || validId <= 0) {
      showMessage("❌ ID ntiyemewe.", "error");
      setDeleteConfirm(null);
      return;
    }
    setLoading(true);
    try {
      await axios.delete(`${API}/${validId}`, getHeaders());
      showMessage("🗑️ Amakuru yasibwe neza!");
      setDeleteConfirm(null);
      await fetchRecords();
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || err.response?.data?.error || "Ntibishoboka gusiba";
      showMessage(`❌ ${errorMsg}`, "error");
      setDeleteConfirm(null);
      setLoading(false);
    }
  };

  const handleView = (record) => {
    setViewRecord(record);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditId(null);
    setShowModal(false);
  };

  const openAddModal = () => {
    setForm(initialForm);
    setEditId(null);
    setShowModal(true);
  };

  const isValidId = (id) => {
    const parsed = parseInt(id, 10);
    return !isNaN(parsed) && parsed > 0;
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  // ─── Filter, Sort & Paginate ───
  const processed = records
    .filter((r) => {
      const matchesSearch =
        r.izina_rya_umuturage?.toLowerCase().includes(search.toLowerCase()) ||
        r.izina_rya_kampani?.toLowerCase().includes(search.toLowerCase()) ||
        r.ukorana_na_kampani_itwara_imyanda
          ?.toLowerCase()
          .includes(search.toLowerCase());

      const matchesFilter =
        filterKampani === "all" ||
        r.ukorana_na_kampani_itwara_imyanda === filterKampani;

      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      const aVal = a[sortField] || "";
      const bVal = b[sortField] || "";
      const cmp =
        typeof aVal === "number"
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal));
      return sortDir === "asc" ? cmp : -cmp;
    });

  const totalPages = Math.ceil(processed.length / perPage);
  const paginated = processed.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  // Stats
  const yegoCount = records.filter(
    (r) => r.ukorana_na_kampani_itwara_imyanda === "Yego"
  ).length;
  const oyaCount = records.filter(
    (r) => r.ukorana_na_kampani_itwara_imyanda === "Oya"
  ).length;
  const invalidIdCount = records.filter((r) => !isValidId(r.id)).length;

  // ─── Responsive Value Helpers ───
  const rv = (watchVal, foldVal, xsVal, smVal, mdVal, lgVal, phabVal, stVal, tabVal, ltVal, sdVal, dVal, ldVal, uwVal) => {
    if (isWatch) return watchVal;
    if (isFoldClosed) return foldVal;
    if (isExtraSmall) return xsVal;
    if (isSmallMobile) return smVal;
    if (isMediumMobile) return mdVal;
    if (isLargeMobile) return lgVal;
    if (isPhablet) return phabVal;
    if (isSmallTablet) return stVal;
    if (isTablet) return tabVal;
    if (isLargeTablet) return ltVal;
    if (isSmallDesktop) return sdVal;
    if (isDesktop) return dVal;
    if (isLargeDesktop) return ldVal;
    if (isUltraWide) return uwVal;
    return dVal;
  };

  // Simplified responsive value (mobile, tablet, desktop)
  const rs = (mobile, tablet, desktop) => {
    if (isMobileAny) return mobile;
    if (isTabletAny) return tablet;
    return desktop;
  };

  // ─── Dynamic Styles ───
  const S = {
    // Global reset style injected
    globalCSS: `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      
      :root { 
        color-scheme: light;
        --safe-top: env(safe-area-inset-top, 0px);
        --safe-bottom: env(safe-area-inset-bottom, 0px);
        --safe-left: env(safe-area-inset-left, 0px);
        --safe-right: env(safe-area-inset-right, 0px);
      }

      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      @keyframes slideUp { 
        from { opacity: 0; transform: translateY(100%); } 
        to { opacity: 1; transform: translateY(0); } 
      }
      @keyframes slideDown { 
        from { opacity: 0; transform: translateY(-20px); } 
        to { opacity: 1; transform: translateY(0); } 
      }
      @keyframes pulse { 
        0%, 100% { opacity: 1; } 
        50% { opacity: 0.5; } 
      }
      @keyframes scaleIn {
        from { opacity: 0; transform: scale(0.9); }
        to { opacity: 1; transform: scale(1); }
      }

      /* Prevent zoom on input focus - ALL mobile devices */
      @media screen and (max-width: 1024px) {
        input[type="text"],
        input[type="search"],
        input[type="email"],
        input[type="tel"],
        input[type="number"],
        select,
        textarea {
          font-size: 16px !important;
        }
      }

      /* Samsung Internet specific fixes */
      @supports (-webkit-overflow-scrolling: touch) {
        input, select, textarea {
          font-size: 16px !important;
        }
      }

      /* iOS Safari fixes */
      @supports (-webkit-touch-callout: none) {
        body { -webkit-text-size-adjust: 100%; }
        input, select { -webkit-appearance: none; }
      }

      /* Scrollbar styling */
      .kurwanya-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
      .kurwanya-scroll::-webkit-scrollbar-track { background: transparent; }
      .kurwanya-scroll::-webkit-scrollbar-thumb { 
        background: rgba(13,27,42,0.15); 
        border-radius: 4px; 
      }
      .kurwanya-scroll { 
        scrollbar-width: thin; 
        scrollbar-color: rgba(13,27,42,0.15) transparent;
        -webkit-overflow-scrolling: touch;
      }

      /* Touch interaction feedback */
      @media (hover: none) and (pointer: coarse) {
        .kurwanya-interactive:active {
          transform: scale(0.97) !important;
          opacity: 0.85;
        }
        * { -webkit-tap-highlight-color: transparent; }
      }

      /* Desktop hover effects */
      @media (hover: hover) and (pointer: fine) {
        .kurwanya-card-hover:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.3);
          border-color: rgba(0,242,96,0.25);
        }
        .kurwanya-btn-hover:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }
      }

      /* Focus visible for keyboard navigation */
      .kurwanya-focusable:focus-visible {
        outline: 2px solid #16a34a;
        outline-offset: 2px;
      }
      .kurwanya-focusable:focus:not(:focus-visible) {
        outline: none;
      }

      /* Landscape phone adjustments */
      @media screen and (max-height: 500px) and (orientation: landscape) {
        .kurwanya-modal-inner {
          max-height: 90vh !important;
          padding: 12px !important;
        }
      }

      /* Print styles */
      @media print {
        body { background: #0d1b2a !important; color: #ffffff !important; }
        .kurwanya-no-print { display: none !important; }
        .kurwanya-card-hover { 
          break-inside: avoid;
          border: 1px solid #cbd5e1 !important;
          box-shadow: none !important;
        }
      }

      /* Galaxy Fold specific */
      @media screen and (min-width: 200px) and (max-width: 300px) {
        .kurwanya-fold-stack { flex-direction: column !important; }
        .kurwanya-fold-full { width: 100% !important; }
      }

      /* Android notch support */
      @media screen and (display-cutout: constant) {
        .kurwanya-container {
          padding-left: max(12px, env(safe-area-inset-left));
          padding-right: max(12px, env(safe-area-inset-right));
        }
      }

      /* High DPI screens */
      @media (-webkit-min-device-pixel-ratio: 3), (min-resolution: 3dppx) {
        .kurwanya-card-hover { 
          border-width: 0.5px; 
        }
      }

      select option {
        background: #f0f4fb;
        color: #0d1b2a;
      }

      button { user-select: none; }
    `,

    container: {
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
      maxWidth: isUltraWide ? "1800px" : isLargeDesktop ? "1600px" : "1400px",
      margin: "0 auto",
      padding: `${rv(4, 6, 8, 10, 12, 12, 14, 16, 18, 20, 20, 24, 24, 32)}px`,
      paddingTop: `calc(${rv(4, 6, 8, 10, 12, 12, 14, 16, 18, 20, 20, 24, 24, 32)}px + var(--safe-top, 0px))`,
      paddingBottom: `calc(${rv(8, 10, 12, 14, 16, 16, 18, 20, 22, 24, 24, 28, 28, 36)}px + var(--safe-bottom, 0px))`,
      paddingLeft: `calc(${rv(4, 6, 8, 10, 12, 12, 14, 16, 18, 20, 20, 24, 24, 32)}px + var(--safe-left, 0px))`,
      paddingRight: `calc(${rv(4, 6, 8, 10, 12, 12, 14, 16, 18, 20, 20, 24, 24, 32)}px + var(--safe-right, 0px))`,
      background: "linear-gradient(135deg, #ffffff 0%, #f0f4fb 50%, #f0f4fb 100%)",
      minHeight: "100vh",
      color: "#f1f5f9",
      overflowX: "hidden",
      WebkitTextSizeAdjust: "100%",
      MozTextSizeAdjust: "100%",
    },

    header: {
      textAlign: "center",
      marginBottom: rs(14, 20, 28),
      padding: `${rs(12, 16, 20)}px ${rs(10, 16, 24)}px`,
      background: "rgba(13,27,42,0.04)",
      borderRadius: rs(12, 14, 16),
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      border: "1px solid rgba(13,27,42,0.08)",
      position: "relative",
      overflow: "hidden",
    },

    headerGlow: {
      position: "absolute",
      top: "-50%",
      left: "50%",
      transform: "translateX(-50%)",
      width: "200%",
      height: "100%",
      background:
        "radial-gradient(ellipse at center, rgba(0,242,96,0.06) 0%, transparent 70%)",
      pointerEvents: "none",
    },

    headerIcon: {
      fontSize: rv(28, 30, 32, 34, 36, 38, 40, 42, 44, 46, 46, 48, 48, 52),
      marginBottom: rs(4, 8, 10),
      display: "block",
      lineHeight: "1",
    },

    title: {
      fontSize: rv(14, 16, 17, 19, 20, 22, 22, 24, 25, 26, 27, 28, 30, 34),
      fontWeight: "800",
      background: "linear-gradient(135deg, #16a34a, #0B3D91, #0369a1)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      margin: "0",
      lineHeight: "1.3",
      letterSpacing: "-0.02em",
      position: "relative",
    },

    subtitle: {
      color: "#64748b",
      fontSize: rv(9, 10, 10, 11, 12, 12, 13, 13, 13, 14, 14, 14, 15, 16),
      marginTop: rs(4, 6, 8),
      lineHeight: "1.5",
      position: "relative",
    },

    statsBar: {
      display: "flex",
      justifyContent: "center",
      gap: rv(4, 5, 6, 7, 8, 10, 10, 12, 14, 16, 18, 20, 20, 24),
      marginBottom: rs(12, 16, 22),
      flexWrap: "wrap",
    },

    statCard: {
      background: "rgba(13,27,42,0.04)",
      borderRadius: rs(8, 10, 12),
      padding: `${rs(8, 10, 12)}px ${rs(12, 18, 24)}px`,
      textAlign: "center",
      border: "1px solid rgba(13,27,42,0.07)",
      minWidth: rv(60, 68, 75, 80, 85, 90, 95, 100, 110, 115, 120, 130, 130, 150),
      flex: isMobileAny ? "1 1 0" : "0 0 auto",
      transition: "all 0.2s ease",
    },

    statNumber: {
      fontSize: rv(14, 16, 17, 18, 19, 20, 20, 22, 22, 24, 24, 26, 28, 32),
      fontWeight: "800",
      background: "linear-gradient(90deg, #16a34a, #0B3D91)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      lineHeight: "1.2",
    },

    statLabel: {
      fontSize: rv(7, 8, 8, 9, 9, 10, 10, 10, 11, 11, 11, 12, 12, 13),
      color: "#64748b",
      marginTop: "3px",
      lineHeight: "1.3",
    },

    toolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: rs(12, 16, 20),
      flexWrap: "wrap",
      gap: rs(8, 10, 12),
      flexDirection: isMobileAny ? "column" : "row",
    },

    searchBox: {
      display: "flex",
      alignItems: "center",
      background: "rgba(13,27,42,0.06)",
      borderRadius: "12px",
      padding: `${rs(10, 10, 12)}px ${rs(12, 14, 16)}px`,
      border: "1px solid rgba(13,27,42,0.1)",
      flex: isMobileAny ? "unset" : "1",
      maxWidth: isMobileAny ? "100%" : "400px",
      width: isMobileAny ? "100%" : "auto",
      transition: "border-color 0.3s ease",
      minHeight: isTouchDevice ? "48px" : "42px",
    },

    searchInput: {
      background: "transparent",
      border: "none",
      outline: "none",
      color: "#0d1b2a",
      fontSize: "16px",
      marginLeft: "10px",
      width: "100%",
      WebkitAppearance: "none",
    },

    toolbarBtns: {
      display: "flex",
      gap: rs(6, 8, 10),
      width: isMobileAny ? "100%" : "auto",
      flexShrink: 0,
    },

    addBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      padding: `${rs(12, 12, 12)}px ${rs(16, 20, 24)}px`,
      background: "linear-gradient(135deg, #16a34a, #0B3D91)",
      color: "#0d1b2a",
      border: "none",
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: rs(13, 13, 14),
      fontWeight: "700",
      transition: "all 0.2s ease",
      boxShadow: "0 4px 20px rgba(0,242,96,0.25)",
      flex: isMobileAny ? "1" : "unset",
      minHeight: isTouchDevice ? "48px" : "42px",
      WebkitTapHighlightColor: "transparent",
      letterSpacing: "0.02em",
    },

    filterBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      padding: `${rs(10, 10, 12)}px ${rs(14, 16, 18)}px`,
      background: showFilters
        ? "rgba(0,242,96,0.15)"
        : "rgba(13,27,42,0.06)",
      color: showFilters ? "#16a34a" : "#94a3b8",
      border: `1px solid ${showFilters ? "rgba(0,242,96,0.3)" : "rgba(13,27,42,0.1)"}`,
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: rs(12, 12, 13),
      fontWeight: "600",
      transition: "all 0.2s ease",
      minHeight: isTouchDevice ? "48px" : "42px",
      WebkitTapHighlightColor: "transparent",
    },

    filterPanel: {
      display: showFilters ? "flex" : "none",
      gap: rs(8, 10, 12),
      marginBottom: rs(12, 14, 18),
      padding: rs(12, 14, 16),
      background: "rgba(13,27,42,0.03)",
      borderRadius: "12px",
      border: "1px solid rgba(13,27,42,0.06)",
      flexWrap: "wrap",
      alignItems: "center",
      animation: "slideDown 0.3s ease",
    },

    filterChip: (active) => ({
      padding: `${rs(8, 8, 8)}px ${rs(14, 16, 18)}px`,
      borderRadius: "20px",
      border: `1px solid ${active ? "rgba(0,242,96,0.4)" : "rgba(13,27,42,0.1)"}`,
      background: active ? "rgba(0,242,96,0.12)" : "rgba(13,27,42,0.04)",
      color: active ? "#16a34a" : "#94a3b8",
      cursor: "pointer",
      fontSize: rs(11, 12, 13),
      fontWeight: active ? "700" : "500",
      transition: "all 0.2s ease",
      minHeight: isTouchDevice ? "40px" : "34px",
      display: "flex",
      alignItems: "center",
      WebkitTapHighlightColor: "transparent",
    }),

    alert: {
      padding: `${rs(10, 12, 14)}px ${rs(14, 18, 24)}px`,
      borderRadius: "12px",
      marginBottom: rs(12, 16, 20),
      fontSize: rs(12, 13, 14),
      fontWeight: "500",
      textAlign: "center",
      animation: "slideDown 0.3s ease",
      lineHeight: "1.5",
    },

    alertSuccess: {
      background: "rgba(0,242,96,0.12)",
      border: "1px solid rgba(0,242,96,0.25)",
      color: "#16a34a",
    },

    alertError: {
      background: "rgba(255,71,87,0.12)",
      border: "1px solid rgba(255,71,87,0.25)",
      color: "#dc2626",
    },

    grid: {
      display: "grid",
      gridTemplateColumns: (() => {
        if (isWatch || isFoldClosed) return "1fr";
        if (isExtraSmall || isSmallMobile || isMediumMobile || isLargeMobile)
          return "1fr";
        if (isPhablet) return isLandscape ? "repeat(2, 1fr)" : "1fr";
        if (isSmallTablet) return "repeat(2, 1fr)";
        if (isTablet) return isLandscape ? "repeat(3, 1fr)" : "repeat(2, 1fr)";
        if (isLargeTablet) return isLandscape ? "repeat(3, 1fr)" : "repeat(2, 1fr)";
        if (isSmallDesktop) return "repeat(3, 1fr)";
        if (isDesktop) return "repeat(3, 1fr)";
        if (isLargeDesktop) return "repeat(4, 1fr)";
        if (isUltraWide) return "repeat(5, 1fr)";
        return "repeat(auto-fill, minmax(340px, 1fr))";
      })(),
      gap: rv(8, 10, 10, 12, 12, 14, 14, 14, 16, 16, 18, 18, 20, 24),
      marginBottom: rs(16, 20, 24),
    },

    card: {
      background: "rgba(13,27,42,0.04)",
      borderRadius: rs(10, 12, 14),
      padding: rv(10, 12, 12, 14, 14, 16, 16, 16, 18, 18, 20, 20, 20, 24),
      border: "1px solid rgba(13,27,42,0.07)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "relative",
      overflow: "hidden",
    },

    cardGlow: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "2px",
      background: "linear-gradient(90deg, transparent, #16a34a, #0B3D91, transparent)",
      opacity: 0.6,
    },

    cardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: rs(8, 12, 14),
      gap: "8px",
    },

    cardName: {
      fontSize: rv(12, 13, 13, 14, 14, 15, 15, 15, 16, 16, 17, 17, 18, 20),
      fontWeight: "700",
      color: "#16a34a",
      margin: "0",
      wordBreak: "break-word",
      flex: "1",
      lineHeight: "1.3",
    },

    cardId: {
      fontSize: rv(8, 8, 9, 9, 10, 10, 10, 10, 11, 11, 11, 11, 12, 12),
      color: "#64748b",
      background: "rgba(13,27,42,0.06)",
      padding: "3px 8px",
      borderRadius: "20px",
      whiteSpace: "nowrap",
      flexShrink: 0,
    },

    cardIdWarning: {
      fontSize: rv(8, 8, 9, 9, 10, 10, 10, 10, 11, 11, 11, 11, 12, 12),
      color: "#dc2626",
      background: "rgba(255,71,87,0.12)",
      padding: "3px 8px",
      borderRadius: "20px",
      whiteSpace: "nowrap",
      flexShrink: 0,
      border: "1px solid rgba(255,71,87,0.25)",
    },

    cardRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: `${rs(5, 6, 7)}px 0`,
      borderBottom: "1px solid rgba(13,27,42,0.04)",
      fontSize: rv(10, 10, 11, 11, 12, 12, 12, 12, 13, 13, 13, 13, 14, 15),
      gap: "8px",
    },

    cardLabel: {
      color: "#64748b",
      fontWeight: "500",
      flex: "1",
      minWidth: 0,
      lineHeight: "1.3",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    cardValue: {
      fontWeight: "600",
      flexShrink: 0,
    },

    badge: {
      padding: `${rv(2, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 3, 4, 4)}px ${rv(6, 7, 8, 9, 10, 10, 11, 11, 12, 12, 12, 12, 14, 16)}px`,
      borderRadius: "20px",
      fontSize: rv(8, 9, 9, 10, 10, 11, 11, 11, 12, 12, 12, 12, 13, 14),
      fontWeight: "700",
      whiteSpace: "nowrap",
      flexShrink: 0,
      letterSpacing: "0.02em",
    },

    badgeYego: {
      background: "rgba(0,242,96,0.12)",
      color: "#16a34a",
      border: "1px solid rgba(0,242,96,0.25)",
    },

    badgeOya: {
      background: "rgba(255,71,87,0.12)",
      color: "#dc2626",
      border: "1px solid rgba(255,71,87,0.25)",
    },

    cardActions: {
      display: "flex",
      gap: rv(3, 4, 4, 5, 6, 6, 6, 7, 8, 8, 8, 8, 10, 12),
      marginTop: rs(8, 10, 12),
      justifyContent: isMobileAny ? "stretch" : "flex-end",
    },

    actionBtn: {
      padding: `${rs(7, 8, 8)}px ${rv(6, 7, 8, 9, 10, 12, 12, 12, 14, 14, 14, 14, 16, 18)}px`,
      borderRadius: "10px",
      border: "none",
      cursor: "pointer",
      fontSize: rv(9, 10, 10, 11, 11, 12, 12, 12, 12, 13, 13, 13, 13, 14),
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: rv(2, 3, 3, 4, 4, 5, 5, 5, 5, 6, 6, 6, 6, 8),
      transition: "all 0.2s ease",
      minHeight: isTouchDevice ? "44px" : "36px",
      flex: isMobileAny ? "1" : "unset",
      WebkitTapHighlightColor: "transparent",
    },

    actionBtnDisabled: {
      opacity: 0.35,
      cursor: "not-allowed",
      pointerEvents: "none",
    },

    viewBtn: {
      background: "rgba(5,117,230,0.15)",
      color: "#0B3D91",
      border: "1px solid rgba(5,117,230,0.25)",
    },

    editBtn: {
      background: "rgba(255,165,0,0.15)",
      color: "#d97706",
      border: "1px solid rgba(255,165,0,0.25)",
    },

    deleteBtn: {
      background: "rgba(255,71,87,0.15)",
      color: "#dc2626",
      border: "1px solid rgba(255,71,87,0.25)",
    },

    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0,0,0,0.8)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      display: "flex",
      justifyContent: "center",
      alignItems: isMobileAny ? "flex-end" : "center",
      zIndex: 100000,
      animation: "fadeIn 0.2s ease",
      padding: isMobileAny ? "0" : "20px",
    },

    modal: {
      background: "linear-gradient(145deg, #f0f4fb, #f0f4fb)",
      borderRadius: isMobileAny ? "20px 20px 0 0" : "16px",
      padding: `${rv(16, 18, 20, 22, 22, 24, 24, 24, 26, 28, 28, 30, 30, 36)}px`,
      width: (() => {
        if (isMobileAny) return "100%";
        if (isPhablet) return "92%";
        if (isSmallTablet) return "80%";
        if (isTablet) return "70%";
        if (isLargeTablet) return "60%";
        if (isSmallDesktop) return "50%";
        return "520px";
      })(),
      maxWidth: isMobileAny ? "100%" : "580px",
      maxHeight: isMobileAny ? "93vh" : isShortScreen ? "95vh" : "88vh",
      overflowY: "auto",
      border: "1px solid rgba(13,27,42,0.08)",
      borderBottom: isMobileAny ? "none" : "1px solid rgba(13,27,42,0.08)",
      boxShadow: "0 30px 80px rgba(0,0,0,0.7)",
      position: "relative",
      WebkitOverflowScrolling: "touch",
      animation: isMobileAny ? "slideUp 0.3s ease" : "scaleIn 0.3s ease",
      paddingBottom: isMobileAny
        ? `calc(${rv(16, 18, 20, 22, 22, 24, 24, 24, 26, 28, 28, 30, 30, 36)}px + var(--safe-bottom, 0px))`
        : undefined,
    },

    modalTitle: {
      fontSize: rv(14, 15, 16, 17, 17, 18, 18, 19, 20, 20, 21, 22, 22, 24),
      fontWeight: "700",
      marginBottom: rs(16, 20, 24),
      textAlign: "center",
      background: "linear-gradient(90deg, #16a34a, #0B3D91)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      paddingRight: "35px",
      paddingLeft: "35px",
      lineHeight: "1.4",
    },

    closeBtn: {
      position: "absolute",
      top: rs(10, 12, 14),
      right: rs(10, 12, 14),
      background: "rgba(255,71,87,0.15)",
      border: "1px solid rgba(255,71,87,0.25)",
      color: "#dc2626",
      borderRadius: "50%",
      width: isTouchDevice ? "42px" : "36px",
      height: isTouchDevice ? "42px" : "36px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontSize: "14px",
      zIndex: 10,
      WebkitTapHighlightColor: "transparent",
      transition: "all 0.2s ease",
    },

    formGroup: {
      marginBottom: rs(14, 16, 18),
    },

    label: {
      display: "block",
      marginBottom: "6px",
      fontSize: rv(10, 11, 11, 12, 12, 12, 12, 12, 13, 13, 13, 13, 14, 14),
      fontWeight: "600",
      color: "#1e293b",
      lineHeight: "1.4",
    },

    input: {
      width: "100%",
      padding: `${rs(12, 12, 14)}px ${rs(12, 14, 16)}px`,
      background: "rgba(13,27,42,0.05)",
      border: "1px solid rgba(13,27,42,0.12)",
      borderRadius: "12px",
      color: "#0d1b2a",
      fontSize: "16px",
      outline: "none",
      transition: "border-color 0.3s ease, box-shadow 0.3s ease",
      WebkitAppearance: "none",
      MozAppearance: "none",
      appearance: "none",
      minHeight: isTouchDevice ? "48px" : "42px",
    },

    radioGroup: {
      display: "flex",
      gap: rv(6, 6, 8, 8, 10, 10, 12, 12, 14, 16, 16, 18, 18, 20),
      marginTop: "6px",
      flexDirection: isWatch || isFoldClosed ? "column" : "row",
    },

    radioLabel: (active) => ({
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      padding: `${rs(10, 10, 12)}px ${rs(14, 16, 20)}px`,
      borderRadius: "12px",
      border: `1px solid ${active ? "rgba(0,242,96,0.4)" : "rgba(13,27,42,0.12)"}`,
      background: active ? "rgba(0,242,96,0.1)" : "rgba(13,27,42,0.03)",
      transition: "all 0.2s ease",
      fontSize: rs(13, 13, 14),
      flex: "1",
      justifyContent: "center",
      minHeight: isTouchDevice ? "48px" : "40px",
      WebkitTapHighlightColor: "transparent",
      userSelect: "none",
      color: active ? "#16a34a" : "#334155",
      fontWeight: active ? "700" : "500",
    }),

    radioInput: {
      accentColor: "#16a34a",
      width: isTouchDevice ? "20px" : "16px",
      height: isTouchDevice ? "20px" : "16px",
    },

    submitBtn: {
      width: "100%",
      padding: `${rs(14, 14, 16)}px`,
      background: "linear-gradient(135deg, #0047AB, #003366)",
      color: "#ffffff",
      border: "none",
      borderRadius: "12px",
      fontSize: rs(14, 15, 16),
      fontWeight: "700",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      marginTop: rs(12, 14, 16),
      transition: "all 0.3s ease",
      boxShadow: "0 4px 25px rgba(0,71,171,0.35)",
      minHeight: isTouchDevice ? "52px" : "46px",
      WebkitTapHighlightColor: "transparent",
      letterSpacing: "0.02em",
    },

    pagination: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: rv(4, 5, 6, 7, 8, 8, 10, 10, 10, 12, 12, 14, 14, 16),
      marginTop: rs(14, 18, 22),
      flexWrap: "wrap",
      padding: `0 ${rs(4, 8, 12)}px`,
    },

    pageBtn: (disabled) => ({
      padding: `${rs(8, 10, 10)}px ${rs(10, 14, 16)}px`,
      background: disabled ? "rgba(13,27,42,0.03)" : "rgba(13,27,42,0.06)",
      border: `1px solid ${disabled ? "rgba(13,27,42,0.05)" : "rgba(13,27,42,0.1)"}`,
      color: disabled ? "#475569" : "#0d1b2a",
      borderRadius: "10px",
      cursor: disabled ? "not-allowed" : "pointer",
      fontSize: rv(10, 10, 11, 11, 12, 12, 12, 12, 13, 13, 13, 13, 13, 14),
      display: "flex",
      alignItems: "center",
      gap: "5px",
      minHeight: isTouchDevice ? "44px" : "38px",
      WebkitTapHighlightColor: "transparent",
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? "none" : "auto",
      transition: "all 0.2s ease",
    }),

    pageInfo: {
      fontSize: rv(10, 10, 11, 11, 12, 12, 12, 12, 13, 13, 13, 13, 14, 14),
      color: "#94a3b8",
      textAlign: "center",
      padding: `0 ${rs(8, 10, 14)}px`,
      whiteSpace: "nowrap",
    },

    emptyState: {
      textAlign: "center",
      padding: `${rs(30, 40, 60)}px ${rs(14, 18, 24)}px`,
      background: "rgba(13,27,42,0.02)",
      borderRadius: "16px",
      border: "1px dashed rgba(13,27,42,0.08)",
    },

    emptyIcon: {
      fontSize: rs(40, 50, 60),
      marginBottom: rs(10, 14, 18),
      display: "block",
      opacity: 0.5,
    },

    emptyText: {
      color: "#64748b",
      fontSize: rs(12, 14, 16),
      lineHeight: "1.6",
    },

    loadingOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0,0,0,0.65)",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 100090,
      gap: "12px",
    },

    spinner: {
      width: rs(36, 42, 48),
      height: rs(36, 42, 48),
      border: "3px solid rgba(255,255,255,0.1)",
      borderTop: "3px solid #16a34a",
      borderRadius: "50%",
      animation: "spin 0.7s linear infinite",
    },

    loadingText: {
      color: "#64748b",
      fontSize: rs(12, 13, 14),
      animation: "pulse 1.5s ease infinite",
    },

    confirmOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0,0,0,0.8)",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      display: "flex",
      justifyContent: "center",
      alignItems: isMobileAny ? "flex-end" : "center",
      zIndex: 100010,
      padding: isMobileAny ? "0" : "20px",
      animation: "fadeIn 0.2s ease",
    },

    confirmBox: {
      background: "linear-gradient(145deg, #f0f4fb, #f0f4fb)",
      borderRadius: isMobileAny ? "20px 20px 0 0" : "16px",
      padding: `${rs(20, 24, 30)}px`,
      paddingBottom: isMobileAny
        ? `calc(${rs(24, 28, 30)}px + var(--safe-bottom, 0px))`
        : `${rs(20, 24, 30)}px`,
      textAlign: "center",
      border: "1px solid rgba(255,71,87,0.25)",
      borderBottom: isMobileAny ? "none" : "1px solid rgba(255,71,87,0.25)",
      maxWidth: "400px",
      width: isMobileAny ? "100%" : "90%",
      animation: isMobileAny ? "slideUp 0.3s ease" : "scaleIn 0.3s ease",
    },

    confirmEmoji: {
      fontSize: rs(32, 36, 40),
      marginBottom: "10px",
      display: "block",
    },

    confirmTitle: {
      fontSize: rs(16, 18, 20),
      fontWeight: "700",
      color: "#dc2626",
      marginBottom: "8px",
    },

    confirmText: {
      color: "#94a3b8",
      fontSize: rs(12, 13, 14),
      marginBottom: rs(18, 22, 25),
      lineHeight: "1.5",
    },

    confirmBtns: {
      display: "flex",
      gap: "12px",
      justifyContent: "center",
      flexDirection: isWatch || isFoldClosed ? "column" : "row",
    },

    confirmYes: {
      padding: `${rs(10, 12, 12)}px ${rs(20, 24, 30)}px`,
      background: "rgba(255,71,87,0.15)",
      color: "#dc2626",
      border: "1px solid rgba(255,71,87,0.35)",
      borderRadius: "10px",
      cursor: "pointer",
      fontWeight: "700",
      fontSize: rs(13, 13, 14),
      minHeight: isTouchDevice ? "48px" : "40px",
      WebkitTapHighlightColor: "transparent",
      flex: isWatch || isFoldClosed ? "1" : "unset",
      transition: "all 0.2s ease",
    },

    confirmNo: {
      padding: `${rs(10, 12, 12)}px ${rs(20, 24, 30)}px`,
      background: "rgba(13,27,42,0.06)",
      color: "#0d1b2a",
      border: "1px solid rgba(13,27,42,0.15)",
      borderRadius: "10px",
      cursor: "pointer",
      fontWeight: "600",
      fontSize: rs(13, 13, 14),
      minHeight: isTouchDevice ? "48px" : "40px",
      WebkitTapHighlightColor: "transparent",
      flex: isWatch || isFoldClosed ? "1" : "unset",
      transition: "all 0.2s ease",
    },

    viewRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: `${rs(8, 10, 12)}px 0`,
      borderBottom: "1px solid rgba(13,27,42,0.05)",
      fontSize: rv(10, 11, 11, 12, 12, 13, 13, 13, 13, 14, 14, 14, 14, 15),
      gap: "10px",
      alignItems: "center",
      flexWrap: isMobileAny ? "wrap" : "nowrap",
    },

    viewLabel: {
      color: "#64748b",
      fontWeight: "500",
      flex: isMobileAny ? "1 1 100%" : "1",
      minWidth: 0,
      lineHeight: "1.4",
    },

    viewValue: {
      color: "#0d1b2a",
      fontWeight: "600",
      textAlign: isMobileAny ? "left" : "right",
      flex: isMobileAny ? "1 1 100%" : "unset",
      wordBreak: "break-word",
      paddingLeft: isMobileAny ? "12px" : "0",
    },

    warningBanner: {
      padding: `${rs(10, 12, 14)}px ${rs(12, 16, 20)}px`,
      borderRadius: "12px",
      marginBottom: rs(12, 14, 18),
      border: "2px solid rgba(255,71,87,0.4)",
      background: "rgba(255,71,87,0.08)",
      color: "#dc2626",
      fontSize: rs(11, 12, 13),
      lineHeight: "1.5",
      textAlign: "center",
    },

    codeBlock: {
      display: "block",
      marginTop: "8px",
      padding: "8px",
      background: "rgba(0,0,0,0.3)",
      borderRadius: "8px",
      fontSize: rv(7, 8, 8, 9, 9, 10, 10, 10, 11, 11, 12, 12, 12, 13),
      wordBreak: "break-all",
      textAlign: "left",
      color: "#dc2626",
      fontFamily: "'Fira Code', 'SF Mono', Monaco, monospace",
    },

    cardWarningBanner: {
      padding: "5px 10px",
      background: "rgba(255,71,87,0.08)",
      borderRadius: "8px",
      fontSize: rv(7, 8, 8, 9, 9, 9, 10, 10, 10, 10, 10, 11, 11, 12),
      color: "#dc2626",
      marginBottom: "8px",
      textAlign: "center",
    },

    sortBtn: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      padding: "6px 10px",
      background: "rgba(13,27,42,0.04)",
      border: "1px solid rgba(13,27,42,0.08)",
      borderRadius: "8px",
      color: "#64748b",
      cursor: "pointer",
      fontSize: rs(10, 11, 12),
      transition: "all 0.2s ease",
      minHeight: isTouchDevice ? "36px" : "30px",
      WebkitTapHighlightColor: "transparent",
    },

    recordCount: {
      fontSize: rs(11, 12, 13),
      color: "#64748b",
      textAlign: "center",
      padding: "8px 0",
    },
  };

  // ─── Badge Component ───
  const Badge = ({ value }) => (
    <span
      style={{
        ...S.badge,
        ...(value === "Yego" ? S.badgeYego : S.badgeOya),
      }}
    >
      {value || "—"}
    </span>
  );

  // ─── Radio Button Group ───
  const RadioGroup = ({ name, value, label }) => (
    <div style={S.formGroup}>
      <label style={S.label}>{label} *</label>
      <div style={S.radioGroup}>
        {["Yego", "Oya"].map((opt) => (
          <label key={opt} style={S.radioLabel(value === opt)}>
            <input
              type="radio"
              name={name}
              value={opt}
              checked={value === opt}
              onChange={handleChange}
              style={S.radioInput}
              className="kurwanya-focusable"
            />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );

  // ─── Get label text based on screen size ───
  const getLabel = (full, short) => {
    if (isWatch || isFoldClosed || isExtraSmall) return short;
    if (isSmallMobile || isMediumMobile) return short;
    return full;
  };

  return (
    <div style={S.container} className="kurwanya-container">
      <style>{S.globalCSS}</style>

      {/* Loading */}
      {loading && createPortal(
        <div style={S.loadingOverlay}>
          <div style={S.spinner} />
          <span style={S.loadingText}>Gutegereza...</span>
        </div>,
        document.body
      )}

      {/* Header */}
      <header style={S.header}>
        <div style={S.headerGlow} />
        <span style={S.headerIcon} role="img" aria-label="recycle">
          ♻️
        </span>
        <h1 style={S.title}>Kurwanya Umwanda</h1>
        <p style={S.subtitle}>
          {isMobileAny
            ? "Gucunga isuku n'umwanda"
            : "Gucunga amakuru yerekeye isuku n'umwanda mu mugi"}
        </p>
      </header>

      {/* Database Warning Banner */}
      {invalidIdCount > 0 && (
        <div style={S.warningBanner}>
          ⚠️ <strong>Ikibazo cya Database:</strong> {invalidIdCount} amakuru
          afite ID = 0.
          <code style={S.codeBlock}>
            ALTER TABLE kurwanya_umwanda MODIFY id int(11) NOT NULL
            AUTO_INCREMENT PRIMARY KEY;
          </code>
        </div>
      )}

      {/* Stats */}
      <div style={S.statsBar}>
        <div style={S.statCard}>
          <div style={S.statNumber}>{records.length}</div>
          <div style={S.statLabel}>
            {getLabel("Abaturage Bose", "Bose")}
          </div>
        </div>
        <div style={S.statCard}>
          <div
            style={{
              ...S.statNumber,
              background: "linear-gradient(90deg, #16a34a, #0369a1)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
            }}
          >
            {yegoCount}
          </div>
          <div style={S.statLabel}>
            {getLabel("Bakorana na Kampani", "Yego")}
          </div>
        </div>
        <div style={S.statCard}>
          <div
            style={{
              ...S.statNumber,
              background: "linear-gradient(90deg, #dc2626, #dc2626)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
            }}
          >
            {oyaCount}
          </div>
          <div style={S.statLabel}>
            {getLabel("Ntabakorana na Kampani", "Oya")}
          </div>
        </div>
      </div>

      {/* Alert */}
      {msg.text && (
        <div
          style={{
            ...S.alert,
            ...(msg.type === "error" ? S.alertError : S.alertSuccess),
          }}
          role="alert"
        >
          {msg.text}
        </div>
      )}

      {/* Toolbar */}
      <div style={S.toolbar} className="kurwanya-no-print">
        <div
          style={S.searchBox}
          onClick={() => searchRef.current?.focus()}
        >
          <FaSearch color="#64748b" size={rs(14, 15, 16)} />
          <input
            ref={searchRef}
            type="text"
            placeholder={
              isMobileAny
                ? "Shakisha..."
                : "Shakisha umuturage cyangwa kampani..."
            }
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            style={S.searchInput}
            className="kurwanya-focusable"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          {search && (
            <FaTimes
              color="#64748b"
              size={14}
              style={{ cursor: "pointer", flexShrink: 0 }}
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
              }}
            />
          )}
        </div>

        <div style={S.toolbarBtns} className="kurwanya-fold-stack">
          <button
            style={S.filterBtn}
            onClick={() => setShowFilters(!showFilters)}
            className="kurwanya-interactive kurwanya-focusable"
          >
            <FaFilter size={12} />
            {!isWatch && !isFoldClosed && "Filter"}
          </button>
          <button
            style={S.addBtn}
            onClick={openAddModal}
            className="kurwanya-interactive kurwanya-focusable kurwanya-btn-hover"
          >
            <FaPlus /> Ongeraho
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      <div style={S.filterPanel}>
        <span
          style={{
            fontSize: rs(11, 12, 13),
            color: "#64748b",
            marginRight: "4px",
          }}
        >
          Kampani:
        </span>
        {[
          { val: "all", label: "Bose" },
          { val: "Yego", label: "Yego" },
          { val: "Oya", label: "Oya" },
        ].map((f) => (
          <button
            key={f.val}
            style={S.filterChip(filterKampani === f.val)}
            onClick={() => {
              setFilterKampani(f.val);
              setCurrentPage(1);
            }}
            className="kurwanya-interactive kurwanya-focusable"
          >
            {f.label}
          </button>
        ))}

        <div style={{ marginLeft: "auto", display: "flex", gap: "6px" }}>
          <button
            style={S.sortBtn}
            onClick={() => toggleSort("izina_rya_umuturage")}
            className="kurwanya-interactive kurwanya-focusable"
          >
            {sortField === "izina_rya_umuturage" && sortDir === "asc" ? (
              <FaSortAmountUp size={10} />
            ) : (
              <FaSortAmountDown size={10} />
            )}
            {!isMobileAny && "Izina"}
          </button>
          <button
            style={S.sortBtn}
            onClick={() => toggleSort("id")}
            className="kurwanya-interactive kurwanya-focusable"
          >
            {sortField === "id" && sortDir === "asc" ? (
              <FaSortAmountUp size={10} />
            ) : (
              <FaSortAmountDown size={10} />
            )}
            ID
          </button>
        </div>
      </div>

      {/* Record count */}
      {search || filterKampani !== "all" ? (
        <div style={S.recordCount}>
          {processed.length} / {records.length} amakuru
        </div>
      ) : null}

      {/* Cards Grid */}
      {paginated.length === 0 ? (
        <div style={S.emptyState}>
          <span style={S.emptyIcon} role="img" aria-label="search">
            🔍
          </span>
          <p style={S.emptyText}>
            {search || filterKampani !== "all"
              ? "Nta makuru ahuye n'ibyo ushakisha"
              : "Nta makuru ahari. Kanda 'Ongeraho' utangire."}
          </p>
        </div>
      ) : (
        <div style={S.grid}>
          {paginated.map((r) => {
            const hasValidId = isValidId(r.id);
            const isExpanded = expandedCard === r.id;

            return (
              <div
                key={`${r.id}-${r.created_at || Math.random()}`}
                style={{
                  ...S.card,
                  ...(!hasValidId
                    ? { border: "1px solid rgba(255,71,87,0.25)" }
                    : {}),
                }}
                className="kurwanya-card-hover kurwanya-interactive"
                onClick={() => {
                  if (isMobileAny) {
                    setExpandedCard(isExpanded ? null : r.id);
                  }
                }}
              >
                <div style={S.cardGlow} />

                <div style={S.cardHeader}>
                  <h3 style={S.cardName}>{r.izina_rya_umuturage}</h3>
                  <span style={hasValidId ? S.cardId : S.cardIdWarning}>
                    #{r.id}
                    {!hasValidId && " ⚠️"}
                  </span>
                </div>

                {!hasValidId && (
                  <div style={S.cardWarningBanner}>
                    ⚠️ ID ntiyemewe - Kosora database
                  </div>
                )}

                <div style={S.cardRow}>
                  <span style={S.cardLabel}>
                    {getLabel("Kampani itwara imyanda", "Kampani")}
                  </span>
                  <Badge value={r.ukorana_na_kampani_itwara_imyanda} />
                </div>

                {r.izina_rya_kampani && (
                  <div style={S.cardRow}>
                    <span style={S.cardLabel}>
                      {getLabel("Izina rya kampani", "Kampani")}
                    </span>
                    <span style={{ ...S.cardValue, color: "#0B3D91" }}>
                      {r.izina_rya_kampani}
                    </span>
                  </div>
                )}

                {/* Show all rows on desktop, or when expanded on mobile */}
                {(!isMobileAny || isExpanded) && (
                  <>
                    <div style={S.cardRow}>
                      <span style={S.cardLabel}>
                        {getLabel("Ufite igikoni", "Igikoni")}
                      </span>
                      <Badge value={r.ufite_igikoni} />
                    </div>

                    <div style={S.cardRow}>
                      <span style={S.cardLabel}>
                        {getLabel("Unywa amazi meza", "Amazi")}
                      </span>
                      <Badge value={r.unywa_amazi_meza} />
                    </div>

                    <div style={S.cardRow}>
                      <span style={S.cardLabel}>
                        {getLabel("Agatanda/Amasahane/Akabati", "Agatanda")}
                      </span>
                      <Badge
                        value={r.ufite_agatanda_kamasahane_cyangwa_akabati}
                      />
                    </div>
                  </>
                )}

                {isMobileAny && !isExpanded && (
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: rv(8, 9, 9, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10),
                      color: "#64748b",
                      padding: "4px 0 0",
                    }}
                  >
                    ▼ Kanda urebe byose
                  </div>
                )}

                <div
                  style={S.cardActions}
                  className="kurwanya-card-actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    style={{ ...S.actionBtn, ...S.viewBtn }}
                    onClick={() => handleView(r)}
                    className="kurwanya-interactive kurwanya-btn-hover kurwanya-focusable"
                    aria-label="View record"
                  >
                    <FaEye />
                    {!isWatch && !isFoldClosed && !isExtraSmall && " Reba"}
                  </button>
                  <button
                    style={{
                      ...S.actionBtn,
                      ...S.editBtn,
                      ...(!hasValidId ? S.actionBtnDisabled : {}),
                    }}
                    onClick={() => hasValidId && handleEdit(r)}
                    className="kurwanya-interactive kurwanya-btn-hover kurwanya-focusable"
                    disabled={!hasValidId}
                    aria-label="Edit record"
                    title={!hasValidId ? "ID ntiyemewe" : "Hindura"}
                  >
                    <FaEdit />
                    {!isWatch && !isFoldClosed && !isExtraSmall && " Hindura"}
                  </button>
                  <button
                    style={{
                      ...S.actionBtn,
                      ...S.deleteBtn,
                      ...(!hasValidId ? S.actionBtnDisabled : {}),
                    }}
                    onClick={() => hasValidId && setDeleteConfirm(r.id)}
                    className="kurwanya-interactive kurwanya-btn-hover kurwanya-focusable"
                    disabled={!hasValidId}
                    aria-label="Delete record"
                    title={!hasValidId ? "ID ntiyemewe" : "Siba"}
                  >
                    <FaTrash />
                    {!isWatch && !isFoldClosed && !isExtraSmall && " Siba"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <nav style={S.pagination} aria-label="Pagination">
          <button
            style={S.pageBtn(currentPage === 1)}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="kurwanya-interactive kurwanya-focusable"
            aria-label="Previous page"
          >
            <FaChevronLeft size={rs(10, 11, 12)} />
            {!isWatch && !isFoldClosed && " Inyuma"}
          </button>

          {/* Page numbers for tablet+ */}
          {!isMobileAny && totalPages <= 7 && (
            <>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    style={{
                      ...S.pageBtn(false),
                      ...(page === currentPage
                        ? {
                            background: "linear-gradient(135deg, #16a34a, #0B3D91)",
                            border: "none",
                            fontWeight: "700",
                          }
                        : {}),
                    }}
                    onClick={() => setCurrentPage(page)}
                    className="kurwanya-interactive kurwanya-focusable"
                  >
                    {page}
                  </button>
                )
              )}
            </>
          )}

          <span style={S.pageInfo}>
            {currentPage} / {totalPages}
            {isDesktopAny && ` (${processed.length})`}
          </span>

          <button
            style={S.pageBtn(currentPage === totalPages)}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="kurwanya-interactive kurwanya-focusable"
            aria-label="Next page"
          >
            {!isWatch && !isFoldClosed && "Imbere "}
            <FaChevronRight size={rs(10, 11, 12)} />
          </button>
        </nav>
      )}

      {/* ─── Form Modal ─── */}
      {showModal && createPortal(
        <div
          style={S.overlay}
          onClick={resetForm}
          role="dialog"
          aria-modal="true"
          aria-label={editId ? "Edit form" : "Add form"}
        >
          <div
            ref={modalRef}
            style={S.modal}
            onClick={(e) => e.stopPropagation()}
            className="kurwanya-scroll kurwanya-modal-inner"
          >
            <button
              style={S.closeBtn}
              onClick={resetForm}
              className="kurwanya-interactive kurwanya-focusable"
              aria-label="Close modal"
            >
              <FaTimes />
            </button>
            <h2 style={S.modalTitle}>
              {editId
                ? `✏️ Hindura (ID: ${editId})`
                : "➕ Ongeraho Amakuru Mashya"}
            </h2>

            <form onSubmit={handleSubmit} noValidate>
              <div style={S.formGroup}>
                <label style={S.label}>Izina ry'umuturage *</label>
                <select
                  name="citizen_id"
                  value={form.citizen_id || ""}
                  onChange={handleCitizenSelect}
                  style={S.input}
                  onFocus={(e) => {
                    e.target.style.borderColor = "rgba(0,242,96,0.5)";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(0,242,96,0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "rgba(13,27,42,0.12)";
                    e.target.style.boxShadow = "none";
                  }}
                  className="kurwanya-focusable"
                >
                  <option value="">-- Hitamo umuturage --</option>
                  {householdHeads.map((c) => (
                    <option key={c.umuturage_id} value={c.umuturage_id}>
                      {c.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <RadioGroup
                name="ukorana_na_kampani_itwara_imyanda"
                value={form.ukorana_na_kampani_itwara_imyanda}
                label={getLabel(
                  "Ukorana na kampani itwara imyanda?",
                  "Kampani itwara imyanda?"
                )}
              />

              {form.ukorana_na_kampani_itwara_imyanda === "Yego" && (
                <div style={S.formGroup}>
                  <label style={S.label}>Izina rya kampani *</label>
                  <input
                    type="text"
                    name="izina_rya_kampani"
                    value={form.izina_rya_kampani}
                    onChange={handleChange}
                    placeholder="Andika izina rya kampani..."
                    style={S.input}
                    onFocus={(e) => {
                      e.target.style.borderColor = "rgba(0,242,96,0.5)";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(0,242,96,0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor =
                        "rgba(13,27,42,0.12)";
                      e.target.style.boxShadow = "none";
                    }}
                    autoComplete="off"
                    className="kurwanya-focusable"
                  />
                </div>
              )}

              <RadioGroup
                name="ufite_igikoni"
                value={form.ufite_igikoni}
                label="Ufite igikoni?"
              />

              <RadioGroup
                name="unywa_amazi_meza"
                value={form.unywa_amazi_meza}
                label="Unywa amazi meza?"
              />

              <RadioGroup
                name="ufite_agatanda_kamasahane_cyangwa_akabati"
                value={form.ufite_agatanda_kamasahane_cyangwa_akabati}
                label={getLabel(
                  "Ufite agatanda k'amasahane cyangwa akabati?",
                  "Agatanda/amasahane/akabati?"
                )}
              />

              <button
                type="submit"
                style={S.submitBtn}
                className="kurwanya-interactive kurwanya-btn-hover kurwanya-focusable"
                disabled={loading}
              >
                <FaSave /> {editId ? "Hindura" : "Emeza"}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* ─── View Modal ─── */}
      {showViewModal && viewRecord && createPortal(
        <div
          style={S.overlay}
          onClick={() => setShowViewModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label="View record details"
        >
          <div
            style={S.modal}
            onClick={(e) => e.stopPropagation()}
            className="kurwanya-scroll kurwanya-modal-inner"
          >
            <button
              style={S.closeBtn}
              onClick={() => setShowViewModal(false)}
              className="kurwanya-interactive kurwanya-focusable"
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <h2 style={S.modalTitle}>📋 Amakuru y'Umuturage</h2>

            <div style={S.viewRow}>
              <span style={S.viewLabel}>ID</span>
              <span
                style={{
                  ...S.viewValue,
                  color: isValidId(viewRecord.id) ? "#0d1b2a" : "#dc2626",
                }}
              >
                #{viewRecord.id}
                {!isValidId(viewRecord.id) && " ⚠️ (Ntiyemewe)"}
              </span>
            </div>
            <div style={S.viewRow}>
              <span style={S.viewLabel}>Izina ry'umuturage</span>
              <span style={S.viewValue}>
                {viewRecord.izina_rya_umuturage}
              </span>
            </div>
            <div style={S.viewRow}>
              <span style={S.viewLabel}>
                {getLabel(
                  "Ukorana na kampani itwara imyanda",
                  "Kampani imyanda"
                )}
              </span>
              <Badge
                value={viewRecord.ukorana_na_kampani_itwara_imyanda}
              />
            </div>
            {viewRecord.izina_rya_kampani && (
              <div style={S.viewRow}>
                <span style={S.viewLabel}>Izina rya kampani</span>
                <span style={{ ...S.viewValue, color: "#0B3D91" }}>
                  {viewRecord.izina_rya_kampani}
                </span>
              </div>
            )}
            <div style={S.viewRow}>
              <span style={S.viewLabel}>Ufite igikoni</span>
              <Badge value={viewRecord.ufite_igikoni} />
            </div>
            <div style={S.viewRow}>
              <span style={S.viewLabel}>Unywa amazi meza</span>
              <Badge value={viewRecord.unywa_amazi_meza} />
            </div>
            <div style={S.viewRow}>
              <span style={S.viewLabel}>
                {getLabel("Agatanda/Amasahane/Akabati", "Agatanda/Akabati")}
              </span>
              <Badge
                value={viewRecord.ufite_agatanda_kamasahane_cyangwa_akabati}
              />
            </div>
            {viewRecord.created_at && (
              <div style={S.viewRow}>
                <span style={S.viewLabel}>Italiki</span>
                <span style={S.viewValue}>
                  {new Date(viewRecord.created_at).toLocaleDateString("rw-RW", {
                    year: "numeric",
                    month: isMobileAny ? "short" : "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ─── Delete Confirm ─── */}
      {deleteConfirm && createPortal(
        <div style={S.confirmOverlay}>
          <div style={S.confirmBox}>
            <span style={S.confirmEmoji} role="img" aria-label="warning">
              ⚠️
            </span>
            <h3 style={S.confirmTitle}>Emeza Gusiba</h3>
            <p style={S.confirmText}>
              Uzi neza ko ushaka gusiba aya makuru (ID: {deleteConfirm})?
              {!isMobileAny && " Ntabwo bishobora gusubizwaho."}
            </p>
            <div style={S.confirmBtns}>
              <button
                style={S.confirmNo}
                onClick={() => setDeleteConfirm(null)}
                className="kurwanya-interactive kurwanya-focusable"
              >
                Oya, Reka
              </button>
              <button
                style={S.confirmYes}
                onClick={() => handleDelete(deleteConfirm)}
                className="kurwanya-interactive kurwanya-focusable"
              >
                Yego, Siba
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default KurwanyaUmwanda;